import { Select } from "@design";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { formatNumber } from "@/i18n/format";
import { type EventFilters, HIT_SOURCES, oneOf, SEVERITIES, useEventLabels } from "./contentSafety";

// No filter can take "*" (category keys are [a-z0-9_]), so it stands for "all".
const ALL = "*";

/** Source, category and severity filters for the events list (the analytics above don't take them). */
export function EventFilterBar({
  filters,
  categories,
  total,
  onChange,
}: Readonly<{
  filters: EventFilters;
  /** The period's categories, from the analytics. */
  categories: string[];
  total?: number;
  onChange: (patch: Partial<EventFilters>) => void;
}>) {
  const { t } = useTranslation("adminContentSafety");
  const labels = useEventLabels();
  // A category from a shared link may not occur in this period: list it anyway so the select shows what's applied.
  const categoryValues =
    filters.category && !categories.includes(filters.category) ? [...categories, filters.category] : categories;

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center">
      <div className="md:w-48">
        <Select
          aria-label={t("filters.source")}
          value={filters.source ?? ALL}
          options={[
            { value: ALL, label: t("filters.allSources") },
            ...HIT_SOURCES.map((source) => ({ value: source, label: labels.source(source) })),
          ]}
          onChange={(value) => onChange({ source: oneOf(HIT_SOURCES, value) })}
        />
      </div>
      <div className="md:w-56">
        <Select
          aria-label={t("filters.category")}
          value={filters.category ?? ALL}
          options={[
            { value: ALL, label: t("filters.allCategories") },
            ...categoryValues.map((category) => ({ value: category, label: labels.category(category) })),
          ]}
          onChange={(value) => onChange({ category: value === ALL ? undefined : value })}
        />
      </div>
      <div className="md:w-48">
        <Select
          aria-label={t("filters.severity")}
          value={filters.severity ?? ALL}
          options={[
            { value: ALL, label: t("filters.allSeverities") },
            ...SEVERITIES.map((severity) => ({ value: severity, label: labels.severity(severity) })),
          ]}
          onChange={(value) => onChange({ severity: oneOf(SEVERITIES, value) })}
        />
      </div>
      {total !== undefined && (
        <Text as="span" variant="body-sm" tone="tertiary" className="md:ml-auto">
          {t("events.total", { count: total, hits: formatNumber(total) })}
        </Text>
      )}
    </div>
  );
}
